import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Upload, Clock, LogIn, LogOut, Lock, ExternalLink, ChevronLeft, UserRound } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useEmbeddedMode } from '../contexts/EmbeddedContext';
import './NavHeader.css';

// When iframed inside knkt-local's Tools tab, ask the host to return to
// the Tools hub. ToolsContainer listens for `knkt:back-to-tools` and
// pops its activeTool state. postMessage (not direct parent access)
// because the iframe is cross-origin.
function backToTools() {
  if (window.parent && window.parent !== window) {
    window.parent.postMessage({ type: 'knkt:back-to-tools' }, '*');
  }
}

export function NavHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, loading } = useAuth();
  const { embedded, standaloneUrl } = useEmbeddedMode();
  const signedIn = Boolean(user);

  // Hide the whole nav on the public /listen page — labels landing from a
  // demo email shouldn't be prompted to sign into KNKT.
  if (location.pathname.startsWith('/listen/')) return null;

  const handleUploadClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!signedIn) {
      navigate('/login', { state: { from: location.pathname } });
      return;
    }
    if (location.pathname === '/') {
      window.dispatchEvent(new CustomEvent('trigger-upload'));
    } else {
      navigate('/');
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('trigger-upload'));
      }, 100);
    }
  };

  return (
    <nav className="nav-header">
      <div className="nav-container">
        {embedded && (
          <button
            type="button"
            className="nav-back-to-tools"
            onClick={backToTools}
            title="Back to Tools hub"
            aria-label="Back to Tools"
          >
            <ChevronLeft size={14} strokeWidth={2.4} />
            <span>Tools</span>
          </button>
        )}
        <Link to="/" className="nav-logo" aria-label="KNKT — home">
          <img src="/knkt-logo.svg" alt="KNKT" className="logo-mark" />
          <span className="logo-text">Audio Agent</span>
        </Link>

        <div className="nav-links">
          <button
            onClick={handleUploadClick}
            className={`nav-link nav-button ${signedIn ? '' : 'nav-link-locked'}`}
            type="button"
            title={signedIn ? 'Upload a track for analysis' : 'Sign in to upload'}
          >
            {signedIn ? (
              <Upload size={14} strokeWidth={2} className="nav-icon" />
            ) : (
              <Lock size={14} strokeWidth={2} className="nav-icon" />
            )}
            Upload Track
          </button>

          {/* History only surfaces in the standalone shell — the
              embedded view stays focused on the current upload+analysis
              flow and links users out to the full app for history. */}
          {signedIn && !embedded && (
            <Link to="/history" className="nav-link">
              <Clock size={14} strokeWidth={2} className="nav-icon" />
              History
            </Link>
          )}

          {/* Human-expert routing. Persistent so the option is always
              one click away — the AI does the volume, an expert does
              the taste-call. */}
          <a
            href="https://book.knkt.io/browse?utm_source=audioagent&utm_medium=nav"
            target="_blank"
            rel="noopener noreferrer"
            className="nav-link"
            title="Get feedback from a KNKT expert artist"
          >
            <UserRound size={14} strokeWidth={2} className="nav-icon" />
            Talk to an expert
          </a>

          {!loading && (
            embedded ? (
              signedIn ? (
                <a
                  href={`${standaloneUrl}${location.pathname}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="nav-link nav-link-auth nav-link-cta"
                  title="Open the full Audio Agent app — full history + standalone view"
                >
                  <ExternalLink size={13} strokeWidth={2.2} className="nav-icon" />
                  Open full Audio Agent
                </a>
              ) : null
            ) : (
              signedIn ? (
                <button
                  type="button"
                  className="nav-link nav-link-auth"
                  onClick={() => { logout(); navigate('/'); }}
                  title={user?.email ? `Signed in as ${user.email}` : 'Sign out'}
                >
                  <LogOut size={14} strokeWidth={2} className="nav-icon" />
                  Sign out
                </button>
              ) : (
                <Link
                  to="/login"
                  state={{ from: location.pathname }}
                  className="nav-link nav-link-auth nav-link-cta"
                >
                  <LogIn size={14} strokeWidth={2} className="nav-icon" />
                  Sign in
                </Link>
              )
            )
          )}
        </div>
      </div>
    </nav>
  );
}
